// Screenshot capture + upload for External portal Focus Items.
//
// A Focus Item (Requirement type 40) can carry screenshots of what the operator
// was looking at when they flagged it. This util rasterizes the DOM; the upload
// itself is the shared requirement-media path (see utils/requirementMediaUpload).
//
// The backend (RequirementMediaViewSet) already gates the row creation to
// customer (portal) create roles, so no backend changes are needed here.
//
// The screenshot is a VISUAL RECORD only — the meaning of the focus item is
// authored by the operator. We do NOT interpret the image.

import { toCanvas, toBlob } from 'html-to-image'
import { uploadRequirementMedia } from './requirementMediaUpload'

// Rasterize a DOM element to a PNG blob. Returns null if the element is missing.
// `ignore` is an optional predicate (el) => bool; matching nodes are EXCLUDED
// from the render (used to keep our own capture overlay out of the screenshot).
//
// Uses html-to-image (SVG foreignObject) rather than html2canvas: the BROWSER
// does the CSS rendering, so modern color functions the app's Tailwind theme
// uses (oklch()) work — html2canvas reimplements CSS parsing and throws on
// oklch.
export async function captureElementPng(el, { scale = 2, ignore = null } = {}) {
  if (!el) return null
  return await toBlob(el, {
    pixelRatio: scale,            // 2x for crisp text on the stored image
    backgroundColor: '#ffffff',
    cacheBust: true,              // avoid stale cross-origin image caching
    // skipFonts: don't try to inline @font-face CSS. html-to-image reads
    // document.styleSheets to embed web fonts, but cross-origin sheets (Google
    // Fonts) throw a SecurityError on cssRules access, aborting the capture.
    // We don't need embedded fonts for a screenshot — the browser already
    // rendered the text.
    skipFonts: true,
    // html-to-image's filter runs per node; return false to drop it. Invert
    // our `ignore` predicate (ignore=true -> filter=false). Always keep nodes
    // when no predicate is supplied.
    filter: ignore ? (node) => !ignore(node) : undefined,
  })
}

// Rasterize a DOM element to a <canvas> (so callers can crop it before
// producing a blob). Same options/rationale as captureElementPng.
export async function captureElementCanvas(el, { scale = 2, ignore = null } = {}) {
  if (!el) return null
  return await toCanvas(el, {
    pixelRatio: scale,
    backgroundColor: '#ffffff',
    cacheBust: true,
    skipFonts: true,   // see captureElementPng — avoids cross-origin font CSS SecurityError
    filter: ignore ? (node) => !ignore(node) : undefined,
  })
}

// Crop a source canvas to a rectangle given in CSS px relative to the captured
// element's top-left, returning a PNG blob. `scale` must match the pixelRatio
// the source canvas was rendered at (the canvas is `scale`x bigger than CSS px).
// The rect is clamped to the canvas bounds; a zero-area rect returns null.
export async function cropCanvasToBlob(sourceCanvas, rectCssPx, scale = 2) {
  if (!sourceCanvas) return null
  const sx = Math.max(0, Math.round(rectCssPx.x * scale))
  const sy = Math.max(0, Math.round(rectCssPx.y * scale))
  const sw = Math.min(sourceCanvas.width - sx, Math.round(rectCssPx.width * scale))
  const sh = Math.min(sourceCanvas.height - sy, Math.round(rectCssPx.height * scale))
  if (sw <= 0 || sh <= 0) return null

  const out = document.createElement('canvas')
  out.width = sw
  out.height = sh
  const ctx = out.getContext('2d')
  ctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, sw, sh)
  return await new Promise(resolve => out.toBlob(resolve, 'image/png'))
}

// Upload an already-captured PNG blob and attach it to a requirement.
// Returns the created RequirementMedia row.
//
// FormData takes a Blob as long as it's given an explicit filename, and the
// name needs a real extension — both MediaView and RequirementMediaViewSet
// gate on it.
export async function uploadFocusScreenshot(requirementId, blob, {
  filename = null,
} = {}) {
  if (!blob) throw new Error('No screenshot blob to upload')

  const name = filename || `focus-${requirementId}-${Date.now()}.png`

  return uploadRequirementMedia({ requirementId, file: blob, filename: name })
}

// Convenience: capture an element and attach it to a requirement in one call.
export async function captureAndAttach(requirementId, el, opts = {}) {
  const blob = await captureElementPng(el, opts)
  if (!blob) return null
  return uploadFocusScreenshot(requirementId, blob, opts)
}
